// Web font containers back to plain OpenType (sfnt), which is what the shaper reads.
// WOFF is zlib-compressed tables; WOFF2 is one Brotli stream with the glyf/loca (and optionally
// hmtx) tables transformed, rebuilt here following the W3C WOFF2 specification, section 5.
// The host supplies the decompressors (Node: zlib; browsers: DecompressionStream for zlib).

export type Inflate = (data: Uint8Array) => Uint8Array | Promise<Uint8Array>;

export interface Decompressors {
  /** zlib (RFC 1950) streams, for WOFF */
  inflate?: Inflate;
  /** Brotli, for WOFF2 */
  brotli?: Inflate;
}

export class FontFormatError extends Error {}

export function fontFormat(b: Uint8Array): 'sfnt' | 'woff' | 'woff2' | null {
  if (b.length < 12) return null;
  const sig = String.fromCharCode(b[0], b[1], b[2], b[3]);
  if (sig === 'wOFF') return 'woff';
  if (sig === 'wOF2') return 'woff2';
  if (sig === 'OTTO' || sig === 'true' || (b[0] === 0 && b[1] === 1 && b[2] === 0 && b[3] === 0)) return 'sfnt';
  return null;
}

/** Plain OpenType data for a TTF, OTF, WOFF or WOFF2 file. */
export async function toSfnt(bytes: Uint8Array, d: Decompressors): Promise<Uint8Array> {
  const f = fontFormat(bytes);
  if (f === 'sfnt') return bytes;
  if (f === 'woff') return woffToSfnt(bytes, d);
  if (f === 'woff2') return woff2ToSfnt(bytes, d);
  throw new FontFormatError('Not a font file (expected TTF, OTF, WOFF or WOFF2)');
}

/** Synchronous variant for hosts with synchronous decompressors (Node's zlib). */
export function toSfntSync(bytes: Uint8Array, d: { inflate?: (b: Uint8Array) => Uint8Array; brotli?: (b: Uint8Array) => Uint8Array }): Uint8Array {
  const f = fontFormat(bytes);
  if (f === 'sfnt') return bytes;
  if (f === 'woff') return woffTables(bytes, (t) => d.inflate!(t)) as Uint8Array;
  if (f === 'woff2') {
    if (!d.brotli) throw new FontFormatError('WOFF2 needs a Brotli decoder');
    return woff2Build(bytes, d.brotli(woff2Compressed(bytes)));
  }
  throw new FontFormatError('Not a font file (expected TTF, OTF, WOFF or WOFF2)');
}

const view = (b: Uint8Array) => new DataView(b.buffer, b.byteOffset, b.byteLength);
const tagAt = (b: Uint8Array, p: number) => String.fromCharCode(b[p], b[p + 1], b[p + 2], b[p + 3]);

// ------------------------------------------------------------------ WOFF 1

async function woffToSfnt(b: Uint8Array, d: Decompressors): Promise<Uint8Array> {
  if (!d.inflate) throw new FontFormatError('WOFF needs a zlib decoder');
  const pending: Promise<void>[] = [];
  const tables = new Map<string, Uint8Array>();
  const dv = view(b);
  const count = dv.getUint16(12);
  for (let i = 0; i < count; i++) {
    const r = 44 + i * 20;
    const tag = tagAt(b, r);
    const off = dv.getUint32(r + 4),
      comp = dv.getUint32(r + 8),
      orig = dv.getUint32(r + 12);
    const data = b.subarray(off, off + comp);
    if (comp < orig) pending.push(Promise.resolve(d.inflate(data)).then((t) => void tables.set(tag, t)));
    else tables.set(tag, data);
  }
  await Promise.all(pending);
  return buildSfnt(dv.getUint32(4), tables);
}

function woffTables(b: Uint8Array, inflate: (t: Uint8Array) => Uint8Array): Uint8Array {
  const tables = new Map<string, Uint8Array>();
  const dv = view(b);
  const count = dv.getUint16(12);
  for (let i = 0; i < count; i++) {
    const r = 44 + i * 20;
    const off = dv.getUint32(r + 4),
      comp = dv.getUint32(r + 8),
      orig = dv.getUint32(r + 12);
    const data = b.subarray(off, off + comp);
    tables.set(tagAt(b, r), comp < orig ? inflate(data) : data);
  }
  return buildSfnt(dv.getUint32(4), tables);
}

// ------------------------------------------------------------------ WOFF 2

const KNOWN_TAGS = 'cmap head hhea hmtx maxp name OS/2 post cvt_ fpgm glyf loca prep CFF_ VORG EBDT EBLC gasp hdmx kern LTSH PCLT VDMX vhea vmtx BASE GDEF GPOS GSUB EBSC JSTF MATH CBDT CBLC COLR CPAL SVG_ sbix acnt avar bdat bloc bsln cvar fdsc feat fmtx fvar gvar hsty just lcar mort morx opbd prop trak Zapf Silf Glat Gloc Feat Sill'
  .split(' ')
  .map((t) => t.replace('_', ' '));

interface W2Entry {
  tag: string;
  origLength: number;
  /** bytes in the decompressed stream */
  length: number;
  transformed: boolean;
}

function woff2Directory(b: Uint8Array): { flavor: number; entries: W2Entry[]; dataStart: number; compressedLength: number } {
  const dv = view(b);
  const flavor = dv.getUint32(4);
  if (flavor === 0x74746366) throw new FontFormatError('WOFF2 font collections are not supported');
  const count = dv.getUint16(12);
  const compressedLength = dv.getUint32(20);
  let p = 48;
  const base128 = () => {
    let v = 0;
    for (let i = 0; i < 5; i++) {
      const x = b[p++];
      if (i === 0 && x === 0x80) throw new FontFormatError('Bad WOFF2 table directory');
      v = v * 128 + (x & 0x7f);
      if (!(x & 0x80)) return v;
    }
    throw new FontFormatError('Bad WOFF2 table directory');
  };
  const entries: W2Entry[] = [];
  for (let i = 0; i < count; i++) {
    const flags = b[p++];
    let tag: string;
    if ((flags & 0x3f) === 0x3f) {
      tag = tagAt(b, p);
      p += 4;
    } else tag = KNOWN_TAGS[flags & 0x3f];
    const origLength = base128();
    const version = flags >> 6;
    // glyf/loca are transformed unless version 3; any other table only with version 1 (hmtx)
    const transformed = tag === 'glyf' || tag === 'loca' ? version !== 3 : version !== 0;
    entries.push({ tag, origLength, length: transformed ? base128() : origLength, transformed });
  }
  return { flavor, entries, dataStart: p, compressedLength };
}

function woff2Compressed(b: Uint8Array): Uint8Array {
  const { dataStart, compressedLength } = woff2Directory(b);
  return b.subarray(dataStart, dataStart + compressedLength);
}

async function woff2ToSfnt(b: Uint8Array, d: Decompressors): Promise<Uint8Array> {
  if (!d.brotli) throw new FontFormatError('WOFF2 needs a Brotli decoder');
  return woff2Build(b, await d.brotli(woff2Compressed(b)));
}

function woff2Build(b: Uint8Array, data: Uint8Array): Uint8Array {
  const { flavor, entries } = woff2Directory(b);
  const raw = new Map<string, Uint8Array>();
  let off = 0;
  for (const e of entries) {
    raw.set(e.tag, data.subarray(off, off + e.length));
    off += e.length;
  }
  const tables = new Map<string, Uint8Array>();
  for (const e of entries) if (!e.transformed) tables.set(e.tag, raw.get(e.tag)!);
  const glyfE = entries.find((e) => e.tag === 'glyf');
  let xMins: Int16Array | null = null;
  if (glyfE?.transformed) {
    const { glyf, loca, xMin } = rebuildGlyf(raw.get('glyf')!);
    tables.set('glyf', glyf);
    tables.set('loca', loca);
    xMins = xMin;
  }
  const hmtxE = entries.find((e) => e.tag === 'hmtx');
  if (hmtxE?.transformed) {
    const hhea = tables.get('hhea');
    const maxp = tables.get('maxp');
    if (!hhea || !maxp || !xMins) throw new FontFormatError('WOFF2 hmtx transform without glyf');
    tables.set('hmtx', rebuildHmtx(raw.get('hmtx')!, view(hhea).getUint16(34), view(maxp).getUint16(4), xMins));
  }
  return buildSfnt(flavor, tables);
}

class Reader {
  p = 0;
  readonly dv: DataView;
  constructor(readonly b: Uint8Array) {
    this.dv = view(b);
  }
  u8() {
    return this.b[this.p++];
  }
  u16() {
    const v = this.dv.getUint16(this.p);
    this.p += 2;
    return v;
  }
  i16() {
    const v = this.dv.getInt16(this.p);
    this.p += 2;
    return v;
  }
  u32() {
    const v = this.dv.getUint32(this.p);
    this.p += 4;
    return v;
  }
  /** 255UInt16 */
  u255() {
    const code = this.u8();
    if (code === 253) return this.u16();
    if (code === 255) return 253 + this.u8();
    if (code === 254) return 506 + this.u8();
    return code;
  }
  bytes(n: number) {
    const v = this.b.subarray(this.p, this.p + n);
    this.p += n;
    return v;
  }
}

/** Growable byte buffer. */
class Writer {
  b = new Uint8Array(1024);
  n = 0;
  private grow(k: number) {
    if (this.n + k <= this.b.length) return;
    const nb = new Uint8Array(Math.max(this.b.length * 2, this.n + k));
    nb.set(this.b.subarray(0, this.n));
    this.b = nb;
  }
  u8(v: number) {
    this.grow(1);
    this.b[this.n++] = v & 0xff;
  }
  u16(v: number) {
    this.grow(2);
    this.b[this.n++] = (v >> 8) & 0xff;
    this.b[this.n++] = v & 0xff;
  }
  u32(v: number) {
    this.grow(4);
    this.b[this.n++] = (v >>> 24) & 0xff;
    this.b[this.n++] = (v >>> 16) & 0xff;
    this.b[this.n++] = (v >>> 8) & 0xff;
    this.b[this.n++] = v & 0xff;
  }
  bytes(src: Uint8Array) {
    this.grow(src.length);
    this.b.set(src, this.n);
    this.n += src.length;
  }
  pad4() {
    while (this.n % 4) this.u8(0);
  }
  done() {
    return this.b.slice(0, this.n);
  }
}

const withSign = (flag: number, v: number) => (flag & 1 ? v : -v);

function rebuildGlyf(t: Uint8Array): { glyf: Uint8Array; loca: Uint8Array; xMin: Int16Array } {
  const h = new Reader(t);
  h.u16(); // reserved
  const optionFlags = h.u16();
  const numGlyphs = h.u16();
  const indexFormat = h.u16();
  const sizes = [h.u32(), h.u32(), h.u32(), h.u32(), h.u32(), h.u32(), h.u32()];
  let at = h.p;
  const stream = (n: number) => {
    const r = new Reader(t.subarray(at, at + n));
    at += n;
    return r;
  };
  const nContour = stream(sizes[0]);
  const nPoints = stream(sizes[1]);
  const flagS = stream(sizes[2]);
  const glyphS = stream(sizes[3]);
  const compS = stream(sizes[4]);
  const bboxS = stream(sizes[5]);
  const instrS = stream(sizes[6]);
  const overlap = optionFlags & 1 ? t.subarray(at, at + ((numGlyphs + 7) >> 3)) : null;
  const bitmapLen = ((numGlyphs + 31) >> 5) << 2;
  const bboxBitmap = bboxS.bytes(bitmapLen);
  const hasBbox = (i: number) => (bboxBitmap[i >> 3] & (0x80 >> (i & 7))) !== 0;

  const out = new Writer();
  const offsets = new Uint32Array(numGlyphs + 1);
  const xMin = new Int16Array(numGlyphs);
  for (let i = 0; i < numGlyphs; i++) {
    offsets[i] = out.n;
    const nc = nContour.i16();
    if (nc === 0) continue; // empty glyph
    if (nc === -1) {
      // composite: copy the component records, then optional instructions
      const start = compS.p;
      let flags: number;
      let instructions = false;
      do {
        flags = compS.u16();
        compS.u16(); // glyph index
        compS.p += flags & 0x0001 ? 4 : 2;
        if (flags & 0x0008) compS.p += 2;
        else if (flags & 0x0040) compS.p += 4;
        else if (flags & 0x0080) compS.p += 8;
        if (flags & 0x0100) instructions = true;
      } while (flags & 0x0020);
      const comp = compS.b.subarray(start, compS.p);
      const bb = [bboxS.i16(), bboxS.i16(), bboxS.i16(), bboxS.i16()];
      xMin[i] = bb[0];
      out.u16(0xffff);
      for (const v of bb) out.u16(v & 0xffff);
      out.bytes(comp);
      if (instructions) {
        const len = glyphS.u255();
        out.u16(len);
        out.bytes(instrS.bytes(len));
      }
      out.pad4();
      continue;
    }
    // simple glyph
    const ends: number[] = [];
    let total = 0;
    for (let c = 0; c < nc; c++) {
      total += nPoints.u255();
      ends.push(total - 1);
    }
    const xs = new Int32Array(total),
      ys = new Int32Array(total);
    const on = new Uint8Array(total);
    let x = 0,
      y = 0;
    for (let k = 0; k < total; k++) {
      let flag = flagS.u8();
      on[k] = flag >> 7 ? 0 : 1;
      flag &= 0x7f;
      let dx: number, dy: number;
      if (flag < 10) {
        dx = 0;
        dy = withSign(flag, ((flag & 14) << 7) + glyphS.u8());
      } else if (flag < 20) {
        dx = withSign(flag, (((flag - 10) & 14) << 7) + glyphS.u8());
        dy = 0;
      } else if (flag < 84) {
        const b0 = flag - 20;
        const b1 = glyphS.u8();
        dx = withSign(flag, 1 + (b0 & 0x30) + (b1 >> 4));
        dy = withSign(flag >> 1, 1 + ((b0 & 0x0c) << 2) + (b1 & 0x0f));
      } else if (flag < 120) {
        const b0 = flag - 84;
        const b1 = glyphS.u8(),
          b2 = glyphS.u8();
        dx = withSign(flag, 1 + (Math.floor(b0 / 12) << 8) + b1);
        dy = withSign(flag >> 1, 1 + (((b0 % 12) >> 2) << 8) + b2);
      } else if (flag < 124) {
        const b1 = glyphS.u8(),
          b2 = glyphS.u8(),
          b3 = glyphS.u8();
        dx = withSign(flag, (b1 << 4) + (b2 >> 4));
        dy = withSign(flag >> 1, ((b2 & 0x0f) << 8) + b3);
      } else {
        const b1 = glyphS.u8(),
          b2 = glyphS.u8(),
          b3 = glyphS.u8(),
          b4 = glyphS.u8();
        dx = withSign(flag, (b1 << 8) + b2);
        dy = withSign(flag >> 1, (b3 << 8) + b4);
      }
      x += dx;
      y += dy;
      xs[k] = x;
      ys[k] = y;
    }
    const instrLen = glyphS.u255();
    const instr = instrS.bytes(instrLen);
    let bb: number[];
    if (hasBbox(i)) bb = [bboxS.i16(), bboxS.i16(), bboxS.i16(), bboxS.i16()];
    else {
      let x0 = Infinity,
        y0 = Infinity,
        x1 = -Infinity,
        y1 = -Infinity;
      for (let k = 0; k < total; k++) {
        x0 = Math.min(x0, xs[k]);
        y0 = Math.min(y0, ys[k]);
        x1 = Math.max(x1, xs[k]);
        y1 = Math.max(y1, ys[k]);
      }
      bb = total ? [x0, y0, x1, y1] : [0, 0, 0, 0];
    }
    xMin[i] = bb[0];
    out.u16(nc);
    for (const v of bb) out.u16(v & 0xffff);
    for (const e of ends) out.u16(e);
    out.u16(instrLen);
    out.bytes(instr);
    // flags and coordinates, uncompressed (every delta as a 16-bit value)
    const overlapBit = overlap && overlap[i >> 3] & (0x80 >> (i & 7)) ? 0x40 : 0;
    for (let k = 0; k < total; k++) out.u8(on[k] | (k === 0 ? overlapBit : 0));
    let px = 0;
    for (let k = 0; k < total; k++) {
      out.u16((xs[k] - px) & 0xffff);
      px = xs[k];
    }
    let py = 0;
    for (let k = 0; k < total; k++) {
      out.u16((ys[k] - py) & 0xffff);
      py = ys[k];
    }
    out.pad4();
  }
  offsets[numGlyphs] = out.n;
  const loca = new Writer();
  for (const o of offsets) {
    if (indexFormat) loca.u32(o);
    else loca.u16(o >> 1);
  }
  return { glyf: out.done(), loca: loca.done(), xMin };
}

function rebuildHmtx(t: Uint8Array, numHMetrics: number, numGlyphs: number, xMin: Int16Array): Uint8Array {
  const r = new Reader(t);
  const flags = r.u8();
  const adv: number[] = [];
  for (let i = 0; i < numHMetrics; i++) adv.push(r.u16());
  const lsb: number[] = [];
  for (let i = 0; i < numHMetrics; i++) lsb.push(flags & 1 ? xMin[i] : r.i16());
  for (let i = numHMetrics; i < numGlyphs; i++) lsb.push(flags & 2 ? xMin[i] : r.i16());
  const w = new Writer();
  for (let i = 0; i < numGlyphs; i++) {
    if (i < numHMetrics) w.u16(adv[i]);
    w.u16(lsb[i] & 0xffff);
  }
  return w.done();
}

// ------------------------------------------------------------------ sfnt writer

function buildSfnt(flavor: number, tables: Map<string, Uint8Array>): Uint8Array {
  const tags = [...tables.keys()].sort();
  const n = tags.length;
  let pow = 1,
    log = 0;
  while (pow * 2 <= n) (pow *= 2), log++;
  const w = new Writer();
  w.u32(flavor);
  w.u16(n);
  w.u16(pow * 16);
  w.u16(log);
  w.u16(n * 16 - pow * 16);
  let off = 12 + n * 16;
  const placed: [Uint8Array, number][] = [];
  for (const tag of tags) {
    const t = tables.get(tag)!;
    for (let i = 0; i < 4; i++) w.u8(tag.charCodeAt(i));
    w.u32(checksum(t));
    w.u32(off);
    w.u32(t.length);
    placed.push([t, off]);
    off += (t.length + 3) & ~3;
  }
  for (const [t] of placed) {
    w.bytes(t);
    w.pad4();
  }
  return w.done();
}

function checksum(t: Uint8Array): number {
  let s = 0;
  for (let i = 0; i < t.length; i += 4) s = (s + (((t[i] << 24) | ((t[i + 1] ?? 0) << 16) | ((t[i + 2] ?? 0) << 8) | (t[i + 3] ?? 0)) >>> 0)) >>> 0;
  return s;
}

// ------------------------------------------------------------------ collections (TTC)

/** The PostScript name (name ID 6) of the font whose table directory starts at `dir`. */
function postscriptNameAt(b: Uint8Array, dir: number): string | null {
  const dv = view(b);
  const count = dv.getUint16(dir + 4);
  for (let i = 0; i < count; i++) {
    const r = dir + 12 + i * 16;
    if (tagAt(b, r) !== 'name') continue;
    const t = dv.getUint32(r + 8);
    const n = dv.getUint16(t + 2),
      strings = t + dv.getUint16(t + 4);
    for (let k = 0; k < n; k++) {
      const e = t + 6 + k * 12;
      const platform = dv.getUint16(e),
        nameId = dv.getUint16(e + 6),
        len = dv.getUint16(e + 8),
        off = strings + dv.getUint16(e + 10);
      if (nameId !== 6) continue;
      if (platform === 3 || platform === 0) {
        let s = '';
        for (let j = 0; j + 1 < len; j += 2) s += String.fromCharCode(dv.getUint16(off + j));
        return s;
      }
      if (platform === 1) return String.fromCharCode(...b.subarray(off, off + len));
    }
  }
  return null;
}

/**
 * One font out of a TrueType/OpenType collection (.ttc, .otc), as a standalone sfnt: the face
 * with this PostScript name, else the one at `index`. Plain fonts come back as they are.
 */
export function collectionFace(b: Uint8Array, which: { postscriptName?: string; index?: number } = {}): Uint8Array {
  if (tagAt(b, 0) !== 'ttcf') return b;
  const dv = view(b);
  const fonts = dv.getUint32(8);
  const dirs = Array.from({ length: fonts }, (_, i) => dv.getUint32(12 + i * 4));
  let pick = which.index ?? 0;
  if (which.postscriptName) {
    const found = dirs.findIndex((d) => postscriptNameAt(b, d) === which.postscriptName);
    if (found >= 0) pick = found;
  }
  const dir = dirs[Math.min(Math.max(0, pick), fonts - 1)];
  const count = dv.getUint16(dir + 4);
  const records = Array.from({ length: count }, (_, i) => {
    const r = dir + 12 + i * 16;
    return { r, offset: dv.getUint32(r + 8), length: dv.getUint32(r + 12) };
  });
  const head = 12 + count * 16;
  const size = records.reduce((s, t) => s + ((t.length + 3) & ~3), head);
  const out = new Uint8Array(size);
  const ov = view(out);
  out.set(b.subarray(dir, dir + 12), 0);
  let at = head;
  records.forEach((t, i) => {
    out.set(b.subarray(t.r, t.r + 8), 12 + i * 16); // tag and checksum
    ov.setUint32(12 + i * 16 + 8, at);
    ov.setUint32(12 + i * 16 + 12, t.length);
    out.set(b.subarray(t.offset, t.offset + t.length), at);
    at += (t.length + 3) & ~3;
  });
  return out;
}

// ------------------------------------------------------------------ style names

const WEIGHTS: [RegExp, number][] = [
  [/hairline|thin/i, 100],
  [/extra ?light|ultra ?light/i, 200],
  [/semi ?light|demi ?light/i, 350],
  [/light/i, 300],
  [/semi ?bold|demi ?bold/i, 600],
  [/extra ?bold|ultra ?bold/i, 800],
  [/heavy|black|ultra/i, 900],
  [/bold/i, 700],
  [/medium/i, 500],
];

/** Weight and italic from a style name ("Semibold Italic" → 600, true). */
export function parseStyleName(style: string): { weight: number; italic: boolean } {
  return { weight: WEIGHTS.find(([re]) => re.test(style))?.[1] ?? 400, italic: /italic|oblique/i.test(style) };
}

